<template>
    <div class="analytics-filters">
        <v-row dense>
            <v-col cols="12" md="6">
                <v-autocomplete ref="survey"
                                :value="survey || null"
                                :items="surveyItems"
                                item-value="id"
                                item-text="label"
                                :label="$t('Surveys')"
                                :placeholder="$t('SurveySearchHint')"
                                :error-messages="surveyError"
                                prepend-inner-icon="mdi-clipboard-text-outline"
                                outlined dense clearable
                                @change="onSurvey">
                    <template #item="{ item }">
                        <v-list-item-content>
                            <v-list-item-title>{{ item.name }}</v-list-item-title>
                            <v-list-item-subtitle>#{{ item.id }}</v-list-item-subtitle>
                        </v-list-item-content>
                    </template>
                </v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
                <v-autocomplete :value="municipalities"
                                :items="municipalityItems"
                                item-value="id"
                                item-text="name"
                                :label="$t('sharedWithMunicipality')"
                                prepend-inner-icon="mdi-city-variant-outline"
                                multiple outlined dense clearable hide-details
                                @change="$emit('update:municipalities', $event || [])">
                    <template #prepend-item>
                        <v-list-item ripple @click="toggleAll">
                            <v-list-item-action>
                                <v-icon :color="municipalities.length ? 'primary' : ''">{{ allIcon }}</v-icon>
                            </v-list-item-action>
                            <v-list-item-content>
                                <v-list-item-title>{{ allSelected ? $t('DeselectAll') : $t('SelectAll') }}</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-1" />
                    </template>
                    <!-- a long selection would push the form down; show two chips and a count -->
                    <template #selection="{ item, index }">
                        <v-chip v-if="index < 2" small close @click:close="remove(item.id)">{{ item.name }}</v-chip>
                        <span v-else-if="index === 2" class="analytics-filters__more">+{{ municipalities.length - 2 }}</span>
                    </template>
                </v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" :md="showIcon ? 4 : 6">
                <v-select :value="year"
                          :items="years"
                          :label="$t('Year')"
                          prepend-inner-icon="mdi-calendar-blank-outline"
                          outlined dense clearable hide-details
                          @change="$emit('update:year', $event || null)" />
            </v-col>
            <v-col cols="12" sm="6" :md="showIcon ? 4 : 6">
                <v-select :value="quarter"
                          :items="quarters"
                          :label="$t('Quarter')"
                          prepend-inner-icon="mdi-calendar-range-outline"
                          outlined dense clearable hide-details
                          @change="$emit('update:quarter', $event || null)" />
            </v-col>
            <v-col v-if="showIcon" cols="12" md="4">
                <v-select :value="icon"
                          :items="icons"
                          item-value="id"
                          :item-text="isArabic ? 'iconTypeNameAr' : 'iconTypeNameEn'"
                          :label="$t('QuestionIconType')"
                          prepend-inner-icon="mdi-shape-outline"
                          outlined dense clearable hide-details
                          @change="$emit('update:icon', $event || null)" />
            </v-col>
        </v-row>
        <!-- page-specific extras (average time, per-question filters) sit above the buttons -->
        <slot />
        <div class="analytics-filters__actions">
            <span v-if="!survey && !surveyError" class="analytics-filters__hint">
                <v-icon small class="me-1">mdi-information-outline</v-icon>{{ $t('ChooseSurveyToViewAnalytics') }}
            </span>
            <v-spacer />
            <v-btn text :disabled="loading" @click="clear">
                <v-icon left small>mdi-filter-remove-outline</v-icon>{{ $t('Clear') }}
            </v-btn>
            <v-btn color="primary" depressed :loading="loading" @click="apply">
                <v-icon left small>mdi-chart-box-outline</v-icon>{{ $t('Apply') }}
            </v-btn>
        </div>
    </div>
</template>

<script>
    /**
     * Filter bar shared by the executive, Sakani and Balady analytics pages. The page keeps the
     * values (bound with .sync) and loads the charts on `apply`, which is only emitted once a
     * survey is chosen - otherwise the survey field itself shows what is missing.
     */
    const FIRST_YEAR = 2018

    export default {
        name: 'AnalyticsFilters',
        props: {
            surveys: { type: Array, default: () => [] },
            municipalityItems: { type: Array, default: () => [] },
            icons: { type: Array, default: () => [] },
            survey: { type: [Number, String], default: null },
            municipalities: { type: Array, default: () => [] },
            year: { type: [Number, String], default: null },
            quarter: { type: String, default: null },
            icon: { type: [Number, String], default: null },
            showIcon: { type: Boolean, default: false },
            loading: { type: Boolean, default: false },
        },
        data() {
            return {
                surveyError: '',
            }
        },
        computed: {
            isArabic() {
                return this.$i18n.locale === 'ar'
            },
            // "id - name" so the survey can be found by its number or by its name in the UI language
            surveyItems() {
                return this.surveys.map(s => {
                    const name = (this.isArabic ? s.nameAr : s.nameEn) || s.nameAr || s.nameEn || ''
                    return { id: s.id, name, label: `${s.id} - ${name}` }
                })
            },
            years() {
                const years = []
                for (let y = new Date().getFullYear(); y >= FIRST_YEAR; y--) years.push(y)
                return years
            },
            quarters() {
                return [
                    { value: 'q1', text: this.$t('QuarterOne') },
                    { value: 'q2', text: this.$t('QuarterTwo') },
                    { value: 'q3', text: this.$t('QuarterThree') },
                    { value: 'q4', text: this.$t('QuarterFour') },
                ]
            },
            allSelected() {
                return this.municipalityItems.length > 0 && this.municipalities.length === this.municipalityItems.length
            },
            allIcon() {
                if (this.allSelected) return 'mdi-close-box'
                return this.municipalities.length ? 'mdi-minus-box' : 'mdi-checkbox-blank-outline'
            },
        },
        methods: {
            onSurvey(id) {
                this.surveyError = ''
                this.$emit('update:survey', id || 0)
            },
            toggleAll() {
                this.$emit('update:municipalities', this.allSelected ? [] : this.municipalityItems.map(x => x.id))
            },
            remove(id) {
                this.$emit('update:municipalities', this.municipalities.filter(x => x !== id))
            },
            apply() {
                if (!this.survey) {
                    this.surveyError = this.$t('ChooseSurveyToViewAnalytics')
                    this.$refs.survey.focus()
                    return
                }
                this.$emit('apply')
            },
            clear() {
                this.surveyError = ''
                this.$emit('clear')
            },
        },
    }
</script>

<style scoped>
    .analytics-filters__actions {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 4px;
    }

    .analytics-filters__hint {
        display: inline-flex;
        align-items: center;
        font-size: 13px;
        color: rgba(0, 0, 0, .6);
    }

    .analytics-filters__more {
        font-size: 13px;
        color: rgba(0, 0, 0, .6);
        margin-inline-start: 4px;
    }
</style>
